import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Plus, ArrowRight, RefreshCw } from 'lucide-react'
import { useAppStore } from '../store'
import { api } from '../lib/api'
import { isValidVaultId } from '../lib/crypto'
import { CONFIG } from '../config'
import { KeyCard, KeyCardStack } from '../components/vault/KeyCard'
import { groupVaultId } from '../lib/folderColors'

export function AddVaultPage() {
  const navigate = useNavigate()
  const bookmarkedVaultIds = useAppStore((s) => s.bookmarkedVaultIds)
  const addBookmarkedVault = useAppStore((s) => s.addBookmarkedVault)

  const [vaultId, setVaultId] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const normalizedId = vaultId.toUpperCase().trim()
  const isValid = isValidVaultId(normalizedId)
  const isAlreadyBookmarked = bookmarkedVaultIds.includes(normalizedId)

  const handleAddVault = async () => {
    if (!isValid || isAlreadyBookmarked || isAdding) return

    setIsAdding(true)
    setError(null)

    try {
      // Check if vault exists
      const meta = await api.getVaultMeta(normalizedId)
      if (!meta) {
        setError('Vault not found. Check the ID and try again.')
        setIsAdding(false)
        return
      }

      // Add to bookmarks
      await api.addBookmark(normalizedId)
      addBookmarkedVault(normalizedId)

      // Navigate to vault to unlock
      navigate(`/v/${normalizedId}`)
    } catch (err) {
      console.error('Failed to add vault:', err)
      setError(err instanceof Error ? err.message : 'Failed to add vault')
    } finally {
      setIsAdding(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
      .toUpperCase()
      .replace(/[^0-9A-HJ-Z]/g, '')
      .slice(0, CONFIG.VAULT_ID_LENGTH)
    setVaultId(value)
    setError(null)
  }

  // Show typed characters on the card, with dots for the rest.
  const preview = groupVaultId(
    (vaultId + '•'.repeat(CONFIG.VAULT_ID_LENGTH)).slice(
      0,
      CONFIG.VAULT_ID_LENGTH,
    ),
  )
  const status = isAlreadyBookmarked
    ? { text: 'Already in your vaults', color: '#F2CC6B' }
    : vaultId && vaultId.length === CONFIG.VAULT_ID_LENGTH && !isValid
      ? { text: 'That isn’t a valid vault ID', color: '#FFA3B4' }
      : null

  return (
    <div className="mx-auto max-w-[440px] pb-8">
      <div className="mb-6 text-center animate-fade-up">
        <h1 className="font-display text-[30px] font-semibold leading-tight tracking-tight sm:text-[38px]">
          Add a vault
        </h1>
        <p className="mt-1 text-muted">
          Enter the 15-character ID someone shared with you.
        </p>
      </div>

      <KeyCardStack spread={isAdding}>
        <KeyCard
          vaultId={preview}
          dim={!vaultId}
          footerLeft={
            isValid
              ? 'Ready to add'
              : `${vaultId.length} of ${CONFIG.VAULT_ID_LENGTH} characters`
          }
          footerRight={
            <span className="inline-flex min-h-[28px] items-center rounded-full bg-white/10 px-3 text-[12.5px] font-bold">
              Locked
            </span>
          }
        />
      </KeyCardStack>

      <form
        className="mt-8 grid gap-3 animate-fade-up"
        style={{ animationDelay: '300ms' }}
        onSubmit={(e) => {
          e.preventDefault()
          handleAddVault()
        }}
      >
        <label htmlFor="vault-id" className="sr-only">
          Vault ID
        </label>
        <input
          id="vault-id"
          type="text"
          value={vaultId}
          onChange={handleInputChange}
          className={`input !min-h-[56px] !rounded-[18px] text-center font-mono text-lg uppercase tracking-[0.2em] ${status?.color === '#FFA3B4' || error ? 'is-err' : ''}`}
          placeholder={'X'.repeat(CONFIG.VAULT_ID_LENGTH)}
          maxLength={CONFIG.VAULT_ID_LENGTH}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          autoFocus
        />
        {(status || error) && (
          <p
            role="alert"
            className="text-center text-sm"
            style={{ color: error ? '#FFA3B4' : status!.color }}
          >
            {error ?? status!.text}
          </p>
        )}
        <button
          type="submit"
          disabled={!isValid || isAlreadyBookmarked || isAdding}
          className="btn-primary min-h-[54px] rounded-[18px] text-[15.5px]"
        >
          {isAdding ? <RefreshCw className="h-5 w-5 animate-spin" /> : null}
          {isAdding ? 'Finding vault…' : 'Add vault'}
          {!isAdding && <ArrowRight className="h-4 w-4" />}
        </button>
      </form>

      <div className="my-8 flex items-center gap-4 text-sm text-faint">
        <span className="h-px flex-1 bg-white/10" />
        or
        <span className="h-px flex-1 bg-white/10" />
      </div>

      <Link
        to="/create"
        className="panel group flex items-center gap-4 p-4 transition-colors hover:border-white/20"
      >
        <span className="kind-ic note" aria-hidden="true">
          <Plus className="h-5 w-5" />
        </span>
        <span className="flex-1">
          <span className="block font-display font-semibold">
            Create a new vault
          </span>
          <span className="block text-sm text-muted">
            Start fresh with your own encrypted vault
          </span>
        </span>
        <ArrowRight className="h-5 w-5 text-muted transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </div>
  )
}
